import { Controller } from 'stimulus';

// Any element with a data-key can activate a tab. The optional scrollTo value
// targets an element on connect; the scrollTo action param targets one on activation.
export default class extends Controller {
  static targets = ['tab', 'panel'];
  static values = {
    urlParam: String,
    activeClasses: String,
    inactiveClasses: String,
    scrollTo: String,
  };

  connect() {
    if (this.scrollToValue) {
      this.#scrollWhenRendered(this.scrollToValue);
    }
  }

  disconnect() {
    this.renderObserver?.disconnect();
  }

  activate(event) {
    this.show(event.currentTarget.dataset.key);
    this.scrollTo(event.params.scrollTo);
  }

  scrollTo(id) {
    if (!id) {
      return;
    }

    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }

  // Inside a closed dialog nothing is laid out yet, so wait until the tabs get a size.
  #scrollWhenRendered(id) {
    this.renderObserver = new ResizeObserver(([entry]) => {
      if (!entry.contentRect.height) {
        return;
      }

      this.renderObserver.disconnect();
      this.scrollTo(id);
    });
    this.renderObserver.observe(this.element);
  }

  show(key) {
    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.key === key;

      tab.classList.remove(
        ...this.classList(active ? this.inactiveClassesValue : this.activeClassesValue)
      );
      tab.classList.add(
        ...this.classList(active ? this.activeClassesValue : this.inactiveClassesValue)
      );
      tab.setAttribute('aria-selected', active);
    });

    this.panelTargets.forEach((panel) => {
      panel.classList.toggle('hidden', panel.dataset.key !== key);
    });

    this.updateUrl(key);
    this.dispatch('shown', { detail: { key } });
  }

  classList(classes) {
    return classes.split(' ').filter(Boolean);
  }

  updateUrl(key) {
    if (!this.urlParamValue) {
      return;
    }

    const url = new URL(window.location);
    url.searchParams.set(this.urlParamValue, key);
    window.history.replaceState({}, '', url);
  }
}
